/**
 * PATH: packages/email-hub/src/feature/compose/DockComposeBody.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPOSE-DOCKCOMPOSEBODY-001
 * PURPOSE: One mounted ComposePage inside the tabbed compose dock. Every
 *          open window stays MOUNTED (hidden via display:none when it is
 *          not the active tab) so switching tabs never loses in-progress
 *          recipients, caret, or attached File objects the 5s autosave
 *          hasn't captured yet.
 * WHY:     Tabbed multi-compose (operator ask 2026-06-15) replaced the
 *          old N-overlapping-mini-windows model; the keep-mounted rule
 *          that minimize relied on now also covers tab switching.
 * RELATED:
 *   - ComposeDock.tsx: owns the dock shell + tab strip; renders one of
 *     these per window and lifts subject/attachment count up for the tab
 *     label + close-confirm.
 *   - pages/ComposePage.tsx: the actual compose form (embed mode).
 */

import { memo } from 'react'
import ComposePage from '../pages/ComposePage'
import {
  useComposeDockStore,
  type ComposeWindow,
} from './composeDockStore'

function overrideProps(w: ComposeWindow) {
  if (w.intent === 'reply') return { replyToIdOverride: w.intentEmailId }
  if (w.intent === 'reply_all') return { replyAllIdOverride: w.intentEmailId }
  if (w.intent === 'forward') return { forwardIdOverride: w.intentEmailId }
  // `new` window → pass its per-window draft id so the compose hook
  // keys its draft slot (local + server) to THIS window, enabling
  // multiple independent new drafts.
  return { draftIdOverride: w.draftId ?? null }
}

interface DockComposeBodyProps {
  w: ComposeWindow
  hidden: boolean
  onSubjectChange: (id: string, subject: string | null) => void
  onAttachCountChange: (id: string, count: number) => void
  /** Routed through the dock so the close-confirm (attachment loss) is
   *  applied consistently from the in-form X and the tab X. */
  onRequestClose: (id: string) => void
  /** A failed send in a background tab must resurface: the dock un-
   *  minimises + selects this tab so the error banner + Retry are seen. */
  onSendFailed: (id: string) => void
}

function DockComposeBody({
  w, hidden, onSubjectChange, onAttachCountChange, onRequestClose, onSendFailed,
}: DockComposeBodyProps) {
  const close = useComposeDockStore((s) => s.close)

  return (
    <div
      role="tabpanel"
      aria-hidden={hidden}
      className="absolute inset-0"
      style={hidden ? { display: 'none' } : undefined}
    >
      <ComposePage
        key={w.id}
        embed
        {...overrideProps(w)}
        initialToOverride={w.initialTo}
        initialSubjectOverride={w.initialSubject}
        initialBodyOverride={w.initialBody}
        onSent={() => close(w.id)}
        onClose={() => onRequestClose(w.id)}
        onSendFailed={() => onSendFailed(w.id)}
        onSubjectChange={(s) => onSubjectChange(w.id, s)}
        onAttachmentsCountChange={(n) => onAttachCountChange(w.id, n)}
      />
    </div>
  )
}

// Memoised: only re-render a body when its window or active/hidden state
// changes — not on every dock drag-move or another tab's subject edit.
// The callback props are stable (useCallback in ComposeDock).
export default memo(DockComposeBody)
